"use client";

import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import EventIcon from "@mui/icons-material/Event";
import { CircularProgress, Dialog, Icon } from "@mui/material";
import SoftButton from "components/SoftButton";
import { Link } from "react-router-dom";
import ActionCell from "./components/ActionCell";
import { useTranslation } from "react-i18next";
import BankApi from "api/bank";

function BankList() {
  const { t } = useTranslation();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const userData = JSON.parse(localStorage.getItem("user"));
  const warehouseId = JSON.parse(localStorage.getItem("selectedWarehouse")).id;
  const role = userData?.business_role_id || null;
  const dashboard_access = role === "warehouse_owner" || role === "admin";

  const handleGetBanks = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found in localStorage.");
      setLoading(false);
      return;
    }

    BankApi.GetBanksList(token)
      .then((response) => {
        if (response.data) {
          setBanks(response.data);
        } else {
          console.warn("No data found:", response.data.msg);
        }
      })
      .catch((error) => {
        if (error.response) {
          console.error("Error response data:", error.response.data.msg);
          console.error("Full error response:", error.response);
        } else {
          console.error("There has been an error.");
        }
        console.log(error.response?.data?.msg || error.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    handleGetBanks();
  }, []);

  const toggleBankSelection = (bankId) => {
    if (!dashboard_access) return;
    setSelectedBanks((prev) =>
      prev.includes(bankId)
        ? prev.filter((id) => id !== bankId)
        : [...prev, bankId]
    );
  };

  const handleAssignBanks = () => {
    const token = localStorage.getItem("token");

    if (selectedBanks?.length === 0) return;

    BankApi.AffectBankToWarehouse(token, warehouseId, selectedBanks)
      .then(() => {
        setSelectedBanks([]);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleDataUpdate = () => {
    handleGetBanks();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {loading ? (
        <SoftBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={35}
        >
          <CircularProgress sx={{ color: "#1F51FF" }} />
        </SoftBox>
      ) : (
        <SoftBox
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          minHeight="85vh"
        >
          <SoftBox py={3}>
            {dashboard_access && (
              <Link to="/gestion-financiere/nouvelle-banque">
                <SoftButton variant="gradient" color="info">
                  + {t("Nouvelle banque")}
                </SoftButton>
              </Link>
            )}
            {dashboard_access && (
              <SoftButton
                variant="gradient"
                color="success"
                onClick={() => setConfirmDialogOpen(true)}
                disabled={selectedBanks?.length === 0}
                sx={{ ml: 2 }}
              >
                {selectedBanks?.length > 0
                  ? `${t("Affecter")} (${selectedBanks?.length})`
                  : t("Affecter les banques")}
              </SoftButton>
            )}

            {selectedBanks?.length > 0 && (
              <SoftBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  mb: 1,
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: "rgba(31, 81, 255, 0.08)",
                  cursor: "pointer",
                }}
                onClick={() => setSelectedBanks([])}
              >
                <Icon sx={{ color: "#1F51FF", mr: 1 }}>check_circle</Icon>
                <SoftTypography variant="body2">
                  {selectedBanks?.length === 1
                    ? `1 ${t("banque sélectionnée")}`
                    : `${selectedBanks?.length} ${t("banques sélectionnées")}`}
                </SoftTypography>
              </SoftBox>
            )}

            <Grid container spacing={3} mt={1}>
              {banks.map((bank, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card
                    onClick={() => toggleBankSelection(bank.id)}
                    sx={{
                      display: "flex",
                      boxShadow: selectedBanks.includes(bank.id)
                        ? "0 8px 16px rgba(31, 81, 255, 0.2)"
                        : "0 2px 8px rgba(0, 0, 0, 0.1)",
                      pt: 1,
                      cursor: "pointer",
                      border: selectedBanks.includes(bank.id)
                        ? "2px solid #1F51FF"
                        : "1px solid #e0e0e0",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      transform: selectedBanks.includes(bank.id)
                        ? "translateY(-4px)"
                        : "none",
                      backgroundColor: selectedBanks.includes(bank.id)
                        ? "rgba(31, 81, 255, 0.05)"
                        : "white",
                      position: "relative",
                      overflow: "visible",
                      "&:hover": {
                        boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
                        borderColor: selectedBanks.includes(bank.id)
                          ? "#1F51FF"
                          : "#1F51FF80",
                      },
                      "&::after": selectedBanks.includes(bank.id)
                        ? {
                            content: '""',
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            backgroundColor: "#1F51FF",
                            backgroundImage:
                              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z'/%3E%3C/svg%3E\")",
                            backgroundSize: "14px",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            zIndex: 1,
                          }
                        : {},
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        pt: selectedBanks?.length > 0 ? 3 : 2,
                      }}
                    >
                      <SoftBox sx={{ width: "90%" }}>
                        <SoftTypography variant="h5" gutterBottom>
                          {bank.name}
                        </SoftTypography>
                        <SoftTypography
                          variant="body2"
                          color="textSecondary"
                          truncate
                        >
                          {t("Numéro de compte")}: {bank.swift_code}
                        </SoftTypography>

                        <SoftBox
                          display="flex"
                          flexDirection={{
                            xs: "column",
                            xxl: "row",
                          }}
                          alignItems={{ xxl: "center" }}
                          justifyContent="space-between"
                          mt={2}
                        >
                          <SoftBox display="flex" alignItems="center">
                            <EventIcon
                              fontSize="small"
                              sx={{ mr: 1, color: "#007bff" }}
                            />
                            <SoftTypography
                              variant="body2"
                              color="textSecondary"
                            >
                              {t("Créé le")}:{" "}
                              {new Date(bank.created_at).toLocaleDateString()}
                            </SoftTypography>
                          </SoftBox>

                          <SoftBox display="flex" alignItems="center">
                            <Icon sx={{ mr: 1, color: "#28a745" }}>
                              location_on
                            </Icon>
                            <SoftTypography
                              variant="body2"
                              color="textSecondary"
                            >
                              {bank.address}
                            </SoftTypography>
                          </SoftBox>
                        </SoftBox>
                      </SoftBox>
                      {dashboard_access && (
                        <ActionCell
                          bankId={bank.id}
                          onDataUpdate={handleDataUpdate}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </SoftBox>
          <Footer />
        </SoftBox>
      )}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <SoftBox p={3}>
          <SoftTypography variant="h6" gutterBottom>
            {t("Confirmation")}
          </SoftTypography>
          <SoftTypography variant="body2" gutterBottom>
            {selectedBanks?.length > 1
              ? t(
                  "Êtes-vous sûr de vouloir affecter les banques à votre entrepôt ?"
                )
              : t(
                  "Êtes-vous sûr de vouloir affecter la banque à votre entrepôt ?"
                )}
          </SoftTypography>
          <SoftBox
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
          >
            <SoftButton
              onClick={() => setConfirmDialogOpen(false)}
              color="secondary"
              variant="text"
              sx={{ mr: 1 }}
            >
              {t("Annuler")}
            </SoftButton>
            <SoftButton
              onClick={() => {
                handleAssignBanks();
                setConfirmDialogOpen(false);
              }}
              color="success"
              variant="contained"
            >
              {t("Confirmer")}
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default BankList;
